import type { NotificationData } from '../../services/notification.service';
import { readSubscriptionPause } from './subscription-pause.util';

type NotificationType = NotificationData['type'];

/** Fields of a `paused_options` item that the Active Subscription popup reads from the payload. */
const PAUSED_OPTION_FIELDS = [
  'subscription_id',
  'order_id',
  'package_id',
  'package_name',
  'pause',
  'total_washes_available',
  'total_washes_redeemed',
  'last_washed_date',
  'wash_count_period',
] as const;

/**
 * A plate whose only membership is paused reaches the lane as `paused-subscription` with one
 * `paused_options` item (CWS-1544). Staff read it in the same popup as an active membership, which
 * already renders the Paused state from the `pause` block, so that single item is re-typed as
 * `active-subscription`. No `auto_redeem` / `gate_opened` is set: the gate stays shut and the
 * lane-gate skip rule must not hide the popup.
 *
 * Several paused memberships (CWS-1469) open the multi-eligible chooser instead: the options already
 * carry its card shape, so staff pick one, tap Activate now, then Redeem it against this read.
 *
 * Anything else (no options, another event) is returned untouched.
 */
export function adaptPausedSubscriptionLpr(
  type: NotificationType,
  data: any
): { type: NotificationType; data: any } {
  if (type !== 'paused-subscription') {
    return { type, data };
  }
  const options = data?.paused_options;
  if (Array.isArray(options) && options.length > 1) {
    return {
      type: 'multi-eligible-selection',
      data: { ...data, purchase_options: options, selection_kind: 'memberships' },
    };
  }
  if (!Array.isArray(options) || options.length !== 1 || !options[0]?.subscription_id) {
    return { type, data };
  }

  const [option] = options as Record<string, unknown>[];
  const fromOption: Record<string, unknown> = {};
  for (const field of PAUSED_OPTION_FIELDS) {
    if (option[field] !== undefined && option[field] !== null) {
      fromOption[field] = option[field];
    }
  }
  return { type: 'active-subscription', data: { ...data, ...fromOption } };
}

/**
 * A paused LPR read whose membership is active again: staff tapped Activate now in the popup, or
 * reopened the read after a resume (CWS-1469). The read can then be redeemed through
 * `multi-eligible-confirm`; the gate opens only from that redeem.
 */
export function isReactivatedPausedRead(data: Record<string, any> | null | undefined): boolean {
  // A live read carries `refusal_reason`; one reopened from the bell only has its stored payload.
  const pausedRead =
    data?.['refusal_reason'] === 'subscription_paused' || Array.isArray(data?.['paused_options']);
  if (!pausedRead) {
    return false;
  }
  const state = readSubscriptionPause(data['pause'])?.state;
  return state === 'resumed' || state === 'none';
}
